import { Component, OnInit, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { isPlatformBrowser } from '@angular/common';
import { Platform } from '@angular/cdk/platform';
import * as Sentry from "@sentry/angular";
import { RouterOutlet } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { EnvironmentValidator } from './utils/environment-validator';
import { ConfigService } from './services/config.service';
import { I18nService } from './i18n/i18n.service';
import { PwaInstallComponent } from './shared/pwa-install/pwa-install.component';
import { PwaInstallPromptComponent } from './shared/pwa-install-prompt/pwa-install-prompt.component';
import { SwUpdate } from '@angular/service-worker';
import { MatSnackBar } from '@angular/material/snack-bar';
import { VdAiBubbleComponent } from './ui/ai-bubble/ai-bubble.component';
import { OnboardingBubbleComponent } from './ui/onboarding-bubble/onboarding-bubble.component';
import { VdSoftphoneWidgetComponent } from './ui/softphone/softphone-widget.component';
import { GuideBubbleComponent } from './ui/guide/guide-bubble.component';
import { ConsentBannerComponent } from './ui/consent-banner/consent-banner.component';
import { VdAppLoaderComponent } from './ui/loader/app-loader.component';
import { DocumentScanBubbleComponent } from './ui/document-scan-bubble/document-scan-bubble.component';
import { WebPushService } from './services/web-push.service';
import { CookieService } from 'ngx-cookie-service';
import { SoftphoneEngineService } from './services/softphone-engine.service';


@Component({
  selector: 'app-root',
  imports: [RouterOutlet, PwaInstallComponent, PwaInstallPromptComponent, VdAiBubbleComponent, OnboardingBubbleComponent, VdSoftphoneWidgetComponent, VdAppLoaderComponent, GuideBubbleComponent, ConsentBannerComponent, DocumentScanBubbleComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit {
  title = 'vender_portal';
  isSidebarCollapsed = false;

  // Force l'instanciation eager de TranslocoService et I18nService dans le
  // main bundle. Sans ces références, Angular CLI tree-shake la lib
  // `@jsverse/transloco` (`sideEffects: false`) et la duplique dans les
  // chunks lazy → 2 instances différentes du token `TRANSLOCO_CONFIG` →
  // `NullInjectorError` au moment où un composant lazy injecte
  // `TranslocoService`. En l'instanciant ici (composant root, toujours
  // dans main), on garantit un singleton partagé par tous les chunks.
  private readonly transloco = inject(TranslocoService);
  private readonly i18n = inject(I18nService);
  private readonly webPush = inject(WebPushService);
  private readonly cookies = inject(CookieService);
  private readonly softphone = inject(SoftphoneEngineService);

  constructor(
    private titleService: Title, 
    private platform: Platform,
    private configService: ConfigService,
    private snackBar: MatSnackBar
  ) {
    // Injection optionnelle de SwUpdate
    try {
      const swUpdate = inject(SwUpdate);
      if (swUpdate && swUpdate.isEnabled) {
        this.swUpdate = swUpdate;
        this.checkForUpdates();
      }
    } catch (e) {
      // SwUpdate n'est pas disponible (mode dev ou non configuré)
      console.log('Service Worker non disponible');
    }
  }

  private swUpdate?: SwUpdate;

  ngOnInit(): void {
    // Valider la configuration d'environnement au démarrage
    EnvironmentValidator.validateAndLog();
    
    // Si la plateforme est le navigateur, appliquez un favicon personnalisé (facultatif)
    if (this.platform.isBrowser) {
      this.changeFavicon('https://cic-iac.s3.eu-west-1.amazonaws.com/codincloud/admin/icon-vendeur-blanc-bleumarine.png'); // Utilisez le chemin correct vers votre favicon
    }

    // Rebind push -> compte connecte (anti-mismatch multi-comptes / meme device).
    // Best-effort, differe pour ne pas concurrencer le boot ; ne s'execute que
    // si un token est present (utilisateur authentifie) et la permission accordee.
    if (this.platform.isBrowser) {
      setTimeout(() => {
        try {
          if (this.cookies.get('access_token')) {
            this.webPush.reconcileCurrentDevice();
          }
        } catch { /* no-op */ }
      }, 4000);
    }
  }

  // Fonction de changement du favicon
  changeFavicon(iconUrl: string): void {
    if (this.platform.isBrowser) {
      const link: HTMLLinkElement = document.createElement('link');
      link.rel = 'icon';
      link.href = iconUrl;

      // Supprimer l'ancienne balise <link> et ajouter la nouvelle
      const head: HTMLElement = document.getElementsByTagName('head')[0];
      const existingLink: HTMLLinkElement | null = document.querySelector('link[rel="icon"]');
      if (existingLink) {
        head.removeChild(existingLink);
      }
      head.appendChild(link);
    }
  }

  // Fonction de test d'erreur pour Sentry
  public throwTestError(): void {
    try {
      throw new Error("Sentry Test Error");
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  // Fonction pour basculer l'état de la sidebar
  toggleSidebar() {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }

  /**
   * Détecte qu'un nouveau Service Worker est passé en ``activated`` et,
   * SEULEMENT s'il s'agit d'une vraie nouvelle version, déclenche un
   * reload (route safe) ou un snackbar (route normale).
   *
   * ANTI-BOUCLE — handshake de version
   * ----------------------------------
   * Avant 2026-06-09 le code faisait reload/snackbar à chaque
   * ``controllerchange`` sans vérifier si la version active du SW
   * différait réellement de la dernière acceptée par cet utilisateur.
   * Combiné au kill-switch ``index.html`` qui désinstallait tous les SW
   * à chaque load et à ``provideServiceWorker(registerWhenStable:30000)``
   * qui les ré-enregistrait, on entrait en boucle reload toutes les ~30 s.
   *
   * Désormais : on demande la version au SW via ``GET_VERSION``
   * (MessageChannel) et on compare avec ``localStorage[vd-sw-last-seen-version]``.
   * - Identique → no-op silencieux.
   * - Différente → on persiste la nouvelle version puis on reload (route
   *   safe) ou on affiche le snackbar (route normale).
   *
   * Pourquoi pas ``SwUpdate.versionUpdates`` ?
   * --------------------------------------
   * Angular SwUpdate s'appuie sur le manifest ``ngsw.json`` généré par
   * Angular Service Worker. Notre SW custom (push/sync/permissions) n'utilise
   * pas ce manifest → ``versionUpdates`` ne se déclenche jamais. Le
   * handshake ``GET_VERSION`` ci-dessous est notre source de vérité.
   *
   * Routes safe : ``/``, ``/login``, ``/signup``, ``/loading``,
   * ``/auth/callback``, ``/auth/landing``. Ailleurs, snackbar non bloquant.
   */
  private static readonly VERSION_KEY = 'vd-sw-last-seen-version';
  private _pendingReload = false;

  private checkForUpdates(): void {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

    // Au boot : si un SW est déjà actif, mémoriser sa version sans
    // prompter (évite un toast intempestif au tout 1er chargement).
    void this._bootstrapVersionSeen();

    // Annonces "sw-version" envoyées spontanément par le SW au moment
    // de l'``activate`` (broadcast à tous les clients). On compare et
    // décide reload/snackbar/no-op.
    navigator.serviceWorker.addEventListener('message', (ev: MessageEvent) => {
      const msg = ev?.data as { type?: string; version?: string } | null;
      if (msg && msg.type === 'sw-version' && typeof msg.version === 'string') {
        this._onSwVersionAnnounced(msg.version);
      }
    });

    // Filet de sécurité : ``controllerchange`` peut survenir avant
    // que le SW ait broadcast sa version. On lui demande explicitement.
    let handled = false;
    navigator.serviceWorker.addEventListener('controllerchange', async () => {
      if (handled) return;
      handled = true;
      const v = await this._askSwVersion();
      if (v) this._onSwVersionAnnounced(v);
      setTimeout(() => { handled = false; }, 5000);
    });

    // Check actif au boot + toutes les 15 min (hotfixes critiques rapides).
    setTimeout(() => this._tryCheckForUpdate(), 5000);
    setInterval(() => this._tryCheckForUpdate(), 15 * 60 * 1000);

    // PWA installée rouverte / onglet refocalisé : c'est LE moment clé. Un
    // déploiement fait pendant que l'app était en arrière-plan doit être
    // récupéré immédiatement (sinon l'utilisateur reste collé sur l'ancien
    // bundle jusqu'au prochain interval, et "vider le cache" ne suffit pas
    // toujours sur une PWA standalone). On re-vérifie + on applique un reload
    // éventuellement en attente.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          void this._tryCheckForUpdate();
          if (this._pendingReload) this._doReload();
        }
      });
    }

    if (this.swUpdate) {
      this.swUpdate.versionUpdates.subscribe(event => {
        if (event.type === 'VERSION_READY') this.promptUserToUpdate();
      });
    }
  }

  /**
   * Appelé quand le SW annonce sa version (broadcast ``activate`` ou
   * réponse à ``GET_VERSION``). Décide quoi faire en comparant avec
   * la dernière version "vue" persistée localement.
   */
  private _onSwVersionAnnounced(swVersion: string): void {
    let lastSeen: string | null = null;
    try { lastSeen = localStorage.getItem(AppComponent.VERSION_KEY); } catch (_) {}

    if (lastSeen === swVersion) {
      // Même version → c'est juste un re-claim (boucle évitée).
      return;
    }

    if (!lastSeen) {
      // Premier boot connu de cette installation — on accepte
      // silencieusement (pas de prompt parasite à la 1ère visite).
      try { localStorage.setItem(AppComponent.VERSION_KEY, swVersion); } catch (_) {}
      console.info('[SW] première version vue:', swVersion);
      return;
    }

    // Vraie nouvelle version → persiste + reload (safe) ou snackbar.
    try { localStorage.setItem(AppComponent.VERSION_KEY, swVersion); } catch (_) {}
    console.info('[SW] nouvelle version SW:', lastSeen, '→', swVersion);

    const path = (typeof window !== 'undefined' ? window.location.pathname : '/') || '/';
    const safeRoutes = ['/', '/login', '/signup', '/loading', '/auth/callback', '/auth/landing'];
    const isSafe = safeRoutes.some((r) => path === r || path.startsWith(r + '/') || path.startsWith(r + '?'));

    if (isSafe) {
      console.info('[SW] reload auto (route safe).');
      setTimeout(() => {
        // Ne pas couper un appel en cours meme sur une route "safe".
        try { if (this.softphone?.isBusy()) { this._scheduleAutoReload(); return; } } catch { /* no-op */ }
        window.location.reload();
      }, 250);
    } else {
      // Route normale : mise à jour AUTOMATIQUE (zéro clic), différée pour ne
      // pas couper une saisie en cours.
      this._scheduleAutoReload();
    }
  }

  /**
   * Demande au SW actif sa version via MessageChannel. Retourne ``null``
   * si pas de SW contrôleur ou pas de réponse en 1500 ms.
   */
  private _askSwVersion(): Promise<string | null> {
    return new Promise<string | null>((resolve) => {
      const sw = navigator.serviceWorker.controller;
      if (!sw) return resolve(null);
      const ch = new MessageChannel();
      const timer = setTimeout(() => resolve(null), 1500);
      ch.port1.onmessage = (ev) => {
        clearTimeout(timer);
        const v = ev?.data?.version;
        resolve(typeof v === 'string' ? v : null);
      };
      try {
        sw.postMessage({ type: 'GET_VERSION' }, [ch.port2]);
      } catch (_) {
        clearTimeout(timer);
        resolve(null);
      }
    });
  }

  /**
   * Au boot, si un SW est déjà actif (cas le plus fréquent) et qu'aucune
   * version n'a encore été persistée, on lit sa version et on aligne
   * ``localStorage[vd-sw-last-seen-version]`` SANS prompter. Le prompt
   * n'aura lieu que sur un futur ``activate`` de SW DIFFÉRENT.
   */
  private async _bootstrapVersionSeen(): Promise<void> {
    if (typeof localStorage === 'undefined') return;
    try {
      const already = localStorage.getItem(AppComponent.VERSION_KEY);
      if (already) return;
      const v = await this._askSwVersion();
      if (v) {
        try { localStorage.setItem(AppComponent.VERSION_KEY, v); } catch (_) {}
      }
    } catch (_) { /* private mode → silencieux */ }
  }

  private async _tryCheckForUpdate(): Promise<void> {
    try {
      if (this.swUpdate && this.swUpdate.isEnabled) {
        await this.swUpdate.checkForUpdate();
      } else if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) await reg.update();
      }
    } catch (_e) { /* offline ou pas de SW : silencieux */ }
  }

  // Demander à l'utilisateur de mettre à jour
  private promptUserToUpdate(): void {
    const snackBarRef = this.snackBar.open(
      'Une nouvelle version de l\'application est disponible',
      'Mettre à jour',
      {
        duration: 0,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      }
    );

    snackBarRef.onAction().subscribe(() => {
      window.location.reload();
    });
  }

  /**
   * Mise à jour AUTOMATIQUE (zéro clic) sur une route normale, sans risque de
   * perdre une saisie en cours : on recharge dès que l'app repasse au premier
   * plan (cf. listener visibilitychange) ; et si l'utilisateur n'est pas en
   * train de taper, on applique vite (court délai). Un snackbar informatif —
   * avec action « Mettre à jour » pour forcer tout de suite — reste affiché.
   */
  private _scheduleAutoReload(): void {
    this._pendingReload = true;

    const ref = this.snackBar.open(
      'Nouvelle version installée — mise à jour automatique…',
      'Mettre à jour',
      { duration: 10000, horizontalPosition: 'center', verticalPosition: 'bottom' },
    );
    ref.onAction().subscribe(() => this._doReload());

    if (!this._isUserTyping()) {
      setTimeout(() => {
        if (this._pendingReload && !this._isUserTyping()) this._doReload();
      }, 2500);
    }
  }

  /** Vrai si un champ de saisie a le focus (on évite de couper la frappe). */
  private _isUserTyping(): boolean {
    if (typeof document === 'undefined') return false;
    const el = document.activeElement as HTMLElement | null;
    if (!el) return false;
    const tag = el.tagName;
    return (
      tag === 'INPUT' ||
      tag === 'TEXTAREA' ||
      tag === 'SELECT' ||
      el.isContentEditable === true
    );
  }

  private _doReload(): void {
    if (!this._pendingReload) return;
    // JAMAIS de reload pendant un appel : recharger couperait le WebSocket SIP
    // et l'appel en cours. On reporte tant que le softphone est occupe.
    try {
      if (this.softphone?.isBusy()) {
        setTimeout(() => this._doReload(), 5000);
        return;
      }
    } catch { /* no-op */ }
    this._pendingReload = false;
    try {
      window.location.reload();
    } catch (_) {
      /* ignore */
    }
  }
}
